import React, { useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons';

const HIGHLIGHT_COLORS = [
  { name: 'Red', value: '#ef4444' },
  { name: 'Orange', value: '#f97316' },
  { name: 'Yellow', value: '#eab308' },
  { name: 'Green', value: '#22c55e' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Blue', value: '#3b82f6' },
  { name: 'Purple', value: '#a855f7' },
  { name: 'Pink', value: '#ec4899' },
];

const ContextMenu = ({ items, position, onClose, isDarkMode }) => {
  const menuRef = useRef(null);
  const closeTimer = useRef(null);
  const [openSubmenu, setOpenSubmenu] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  useEffect(() => {
    if (!menuRef.current) return;
    const rect = menuRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (rect.right > vw) menuRef.current.style.left = `${position.x - rect.width}px`;
    if (rect.bottom > vh) menuRef.current.style.top = `${position.y - rect.height}px`;
  }, [position]);

  if (!position) return null;

  return (
    <div
      ref={menuRef}
      className={`fixed z-[9999] rounded-lg shadow-xl border py-1 min-w-[180px] ${
        isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
      }`}
      style={{ left: position.x, top: position.y }}
    >
      {items.map((item, i) => {
        if (item.divider) {
          return <div key={i} className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`} />;
        }

        // Color picker row
        if (item.colorPicker) {
          return (
            <div key={i} className={`px-3 py-1.5 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <div className="text-[10px] mb-1">{item.label || 'Highlight'}</div>
              <div className="flex items-center gap-1">
                {HIGHLIGHT_COLORS.map(c => (
                  <button
                    key={c.value}
                    onClick={() => { item.onSelect(c.value); onClose(); }}
                    className="w-4 h-4 rounded-sm border border-transparent hover:border-white hover:scale-125 transition-transform"
                    style={{ backgroundColor: c.value, outline: item.currentColor === c.value ? '2px solid white' : 'none' }}
                    title={c.name}
                  />
                ))}
                {item.currentColor && (
                  <button
                    onClick={() => { item.onSelect(null); onClose(); }}
                    className={`ml-1 text-[9px] px-1 rounded ${isDarkMode ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                    title="Clear color"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          );
        }

        // Submenu item
        if (item.submenu) {
          return (
            <div key={i} className="relative">
              <button
                onMouseEnter={(e) => {
                  clearTimeout(closeTimer.current);
                  const rect = e.currentTarget.getBoundingClientRect();
                  setOpenSubmenu(i);
                  setSubmenuPosition({
                    x: rect.right,
                    y: rect.top
                  });
                }}
                onMouseLeave={(e) => {
                  const relatedTarget = e.relatedTarget;
                  if (!relatedTarget || !relatedTarget.closest('.submenu-container')) {
                    closeTimer.current = setTimeout(() => {
                      setOpenSubmenu(prev => prev === i ? null : prev);
                    }, 150);
                  }
                }}
                disabled={item.disabled}
                className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
                  item.disabled
                    ? (isDarkMode ? 'text-gray-600 cursor-default' : 'text-gray-300 cursor-default')
                    : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50')
                }`}
              >
                {item.icon && <FontAwesomeIcon icon={item.icon} className="w-3 text-[10px]" />}
                <span className="flex-1">{item.label}</span>
                <FontAwesomeIcon icon={faChevronRight} className="w-2 text-[8px] ml-2" />
              </button>

              {/* Submenu */}
              {openSubmenu === i && submenuPosition && (
                <div
                  className={`submenu-container fixed z-[10000] rounded-lg shadow-xl border py-1 min-w-[180px] ${
                    isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-200'
                  }`}
                  style={{ left: submenuPosition.x, top: submenuPosition.y }}
                  onMouseEnter={() => { clearTimeout(closeTimer.current); setOpenSubmenu(i); }}
                  onMouseLeave={() => { closeTimer.current = setTimeout(() => setOpenSubmenu(null), 150); }}
                >
                  {item.submenu.map((subitem, si) => {
                    if (subitem.divider) {
                      return <div key={si} className={`my-1 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-100'}`} />;
                    }

                    return (
                      <button
                        key={si}
                        onClick={() => { subitem.onClick(); onClose(); }}
                        disabled={subitem.disabled}
                        className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
                          subitem.disabled
                            ? (isDarkMode ? 'text-gray-600 cursor-default' : 'text-gray-300 cursor-default')
                            : subitem.danger
                              ? (isDarkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50')
                              : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50')
                        }`}
                      >
                        {subitem.icon && <FontAwesomeIcon icon={subitem.icon} className="w-3 text-[10px]" />}
                        <span>{subitem.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        // Regular menu item
        return (
          <button
            key={i}
            onClick={() => { item.onClick(); onClose(); }}
            disabled={item.disabled}
            className={`w-full text-left px-3 py-1.5 text-xs flex items-center gap-2 ${
              item.disabled
                ? (isDarkMode ? 'text-gray-600 cursor-default' : 'text-gray-300 cursor-default')
                : item.danger
                  ? (isDarkMode ? 'text-red-400 hover:bg-red-900/30' : 'text-red-600 hover:bg-red-50')
                  : (isDarkMode ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-50')
            }`}
          >
            {item.icon && <FontAwesomeIcon icon={item.icon} className="w-3 text-[10px]" />}
            <span>{item.label}</span>
            {item.shortcut && (
              <span className={`ml-auto text-[9px] ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>{item.shortcut}</span>
            )}
          </button>
        );
      })}
    </div>
  );
};

export default ContextMenu;
